import { makeAutoObservable, reaction } from "mobx"
import { ContestantWithBio, ICompetition, IPayoutOptions, ISteps, Status } from "../utils/interfaces"
import { COMPETITION_BANNER_S3_FOLDER, COMPETITION_LOGO_S3_FOLDER, premierSteps, seasonTypes, status, steps } from "../utils/constants"
import { getPaystackReference, retrieveErrorMessage, uploadImage } from "../utils/helpers"
import { createCompetitionRequest, savePayoutOptionsRequest, setCompetitionSubscriptionPlanRequest, setSeasonPaymentRequest, updateCompetitionRequest } from "../utils/apiRequests"
import uiStore from "./uiStore"
import contestantStore from "./contestantStore"
import cloneDeep from "lodash/cloneDeep"
import { stepTitles } from "../pages/VoterDashboard/CreateCompetition/utils"
import paymentStore from "./paymentStore"
import authStore from "./authStore"
import competitionStore from "./competitionStore"

type TCreateCompetitionData = {
  competitionDetails: ICompetition
  payoutOptions: IPayoutOptions
  currentStep: number
  stepTitle: string
  steps: ISteps
  status: TStatus 
  errorMessages: TErrorMessages
}

type TStatus = {
  createCompetition?: Status
  setSubscriptionPlan?: Status
  payoutOptions?: Status
}

type TErrorMessages = {
  createCompetition: string
  setSubscriptionPlan: string
  payoutOptions: string
}

type TSetSubscriptionPlan = {
  competitionUsername: string,
  subscriptionPlan: number,
  reference?: string,
  amount?: number,
  seasonId?: string,
}

const defaultCompetitionFields = {
  competitionDetails: {} as ICompetition,
  payoutOptions: {} as IPayoutOptions,
  currentStep: 0,
  stepTitle: '',
  steps: {...steps},
  status: {
    createCompetition: undefined,
    setSubscriptionPlan: undefined,
    payoutOptions: undefined
  },
  errorMessages: {
    createCompetition: '',
    setSubscriptionPlan: '',
    payoutOptions: ''
  }
} as TCreateCompetitionData

class CreateCompetitionStore {
  data: TCreateCompetitionData = cloneDeep(defaultCompetitionFields)

  constructor() {
    makeAutoObservable(this)
  }

  setCurrentStep = (step: number) => {
    this.data.currentStep = step
  }

  creatingCompetition = () => {
    this.data.status.createCompetition = status.LOADING
    this.data.errorMessages.createCompetition = ''
  }

  creatingFailed = (e: any) => {
    this.data.status.createCompetition = status.FAILED
    uiStore.errorModal(retrieveErrorMessage(e))
  }

  creatingSucceeded = (responseData: ICompetition) => {
    this.data.status.createCompetition = status.COMPLETE
    this.data.competitionDetails = responseData
    const successMessage = `${this.competitionCreated ? 'Competition Updated' : 'Competition Created'}, click next to continue.`
    uiStore.showToastMessage({ type: 'success', message: successMessage })
    localStorage.setItem('create_competition', JSON.stringify({...this.data}))
    this.nextStep()
  }

  nextStep = () => {
    this.data.currentStep++
  }
  
  settingSubscriptionPlan = () => {
    this.data.status.setSubscriptionPlan = status.LOADING
    this.data.errorMessages.setSubscriptionPlan = ''
  }

  setSubscriptionPlanSucceeded = (subscriptionPlan: number) => {
    this.data.status.setSubscriptionPlan = status.COMPLETE
    this.data.competitionDetails.pricing_subscription = subscriptionPlan
    this.data.competitionDetails.paid = true
    localStorage.setItem('create_competition', JSON.stringify({...this.data}))
    uiStore.showToastMessage({ type: 'success', message: 'Subscription plan set successfully' })
  }

  updateStepsBasedOnSubscriptionPlan = (subscriptionPlan: number) => {
    if (subscriptionPlan === seasonTypes.PREMIER) {
      this.data.steps = {...premierSteps}
    } else {
      this.data.steps = {...steps}
    }
  }

  setSubscriptionPlanFailed = (e: any) => {
    this.data.status.setSubscriptionPlan = status.FAILED
    uiStore.errorModal(retrieveErrorMessage(e))
  }

  saveContestant = (payload: ContestantWithBio) => {
    if (!this.seasonId) return uiStore.errorModal('Season ID is required to save a contestant.');
    contestantStore.saveContestant({
      payload,
      currentSeasonId: this.seasonId || '',
      creatingCompetition: true,
      subscriptionPlan: this.subscriptionPlan,
    })
  }

  savingPayoutOptions = () => {
    this.data.status.payoutOptions = status.LOADING
    this.data.errorMessages.payoutOptions = ''
  }

  savePayoutOptionsSucceeded = (payload: IPayoutOptions) => {
    this.data.status.payoutOptions = status.COMPLETE
    this.data.payoutOptions = payload
    localStorage.setItem('create_competition', JSON.stringify({...this.data}))
    uiStore.showToastMessage({ type: 'success', message: 'Payout options saved successfully' })
    this.nextStep()
  }

  savePayoutOptionsFailed = (e: any) => {
    this.data.status.payoutOptions = status.FAILED
    uiStore.errorModal(retrieveErrorMessage(e))
  }

  resetCompetitionFields = () => {
    this.data = cloneDeep(defaultCompetitionFields)
  }

  setStepTitle = (title: string) => {
    this.data.stepTitle = title
  }

  setSteps = (steps: ISteps) => {
    this.data.steps = steps
  }

  loadExistingCompetition() {
    const payload = localStorage.getItem('create_competition')
    if (!payload) return

    this.data = JSON.parse(payload)
    this.isPremier ? this.setSteps({...premierSteps}) : this.setSteps({...steps})
    this.setCurrentStep(steps.SEASON_TYPE_CHOOSER)
  }

  onSubscriptionTypeSelected = (plan: number) => {
    const competitionUsername = this.competitionDetails.username
    const subscriptionCost = competitionStore.getPlanByCode(plan)?.price || 0

    if (!subscriptionCost) {
      this.setSubscriptionPlan({ competitionUsername, subscriptionPlan: plan })
      return
    }
    
    uiStore.openConfirmDialog('Are you sure you want to select this plan? It can\'t be undone', () => {
      paymentStore.startPayment({
        email: authStore.userEmail,
        reference: getPaystackReference(authStore.username),
        amount: subscriptionCost,
        onSuccess: (data) => {
          const { status, reference } = data
          if (status === 'success') {
            this.setSubscriptionPlan({
              competitionUsername,
              subscriptionPlan: plan,
              amount: subscriptionCost,
              seasonId: this.seasonId,
              reference,
            })
          }
        }
      })
    })
  }

  async prepareLogo(requestPayload: ICompetition, logo?: string) {
    const { username } = requestPayload
    if (logo && (logo !== this.competitionDetails.logo)) {
      const logoImageToRefresh = document.getElementsByClassName("competition-logo")
      requestPayload.logo = await uploadImage({
        key: username,
        imageUrl: logo,
        folderPath: COMPETITION_LOGO_S3_FOLDER,
        imageElementsToRefresh: logoImageToRefresh
      })
    }

    return requestPayload
  }

  async prepareBanner(requestPayload: ICompetition, banner?: string) {
    const { username } = requestPayload
    if (banner && (banner !== this.competitionDetails.banner)) {
      const bannerImageToRefresh = document.getElementsByClassName("competition-banner")
      requestPayload.banner = await uploadImage({
        key: username,
        imageUrl: banner,
        folderPath: COMPETITION_BANNER_S3_FOLDER,
        imageElementsToRefresh: bannerImageToRefresh
      })
    }

    return requestPayload
  }

  async prepareBannerAndLogo(requestPayload: ICompetition, banner?: string, logo?: string) {
    await Promise.all([this.prepareLogo(requestPayload, logo), this.prepareBanner(requestPayload, banner)])
  }

  async createCompetition(payload: ICompetition) {
    if (this.createCompetitionLoading) return
    this.creatingCompetition()

    const { logo, banner, ...rest } = payload
    const requestPayload: ICompetition = {...rest}

    try {
      await this.prepareBannerAndLogo(requestPayload, banner, logo)
      const response = this.competitionCreated 
        ? await updateCompetitionRequest(requestPayload, this.competitionDetails.username) 
        : await createCompetitionRequest(requestPayload)

      this.creatingSucceeded(response.data)
      this.nextStep()
    } catch (e) {
      this.creatingFailed(e)
    }

    uiStore.setLoadingScreenMessage('')
  }

  async setSubscriptionPlan({
    competitionUsername,
    subscriptionPlan,
    reference,
    amount,
    seasonId
  }: TSetSubscriptionPlan) {
    if (this.setSubscriptionPlanLoading) return
    this.settingSubscriptionPlan()

    try {
      if (amount && seasonId && reference) await setSeasonPaymentRequest(seasonId, { amount, reference })
      await setCompetitionSubscriptionPlanRequest({
        competition_id: competitionUsername,
        pricing_subscription: subscriptionPlan
      })
      this.setSubscriptionPlanSucceeded(subscriptionPlan)
      this.updateStepsBasedOnSubscriptionPlan(subscriptionPlan)
    } catch (e) {
      this.setSubscriptionPlanFailed(e)
    }
  }

  async savePayoutOptions(payload: IPayoutOptions) {
    if (this.savePayoutOptionsLoading) return
    this.savingPayoutOptions()

    try {
      await savePayoutOptionsRequest(this.competitionDetails.username, payload)
      this.savePayoutOptionsSucceeded(payload)
    } catch (e) {
      this.savePayoutOptionsFailed(e)
    }
  }

  get status() {
    return this.data.status
  }

  get createCompetitionLoading() {
    return this.status.createCompetition === status.LOADING
  }

  get competitionDetails() {
    return this.data.competitionDetails
  }

  get competitionCreated() {
    return !!this.competitionDetails.id
  }

  get setSubscriptionPlanLoading() {
    return this.status.setSubscriptionPlan === status.LOADING
  }

  get savePayoutOptionsLoading() {
    return this.status.payoutOptions === status.LOADING
  }

  get subscriptionPlan() {
    return this.competitionDetails.pricing_subscription || seasonTypes.FREE
  }

  get hidePrevButton() {
    return this.data.currentStep === 0
  }

  get hideNextButton() {
    return this.data.currentStep >= Object.keys(this.data.steps).length - 1
  }

  get steps() {
    return this.data.steps
  }

  get currentStep() {
    return this.data.currentStep
  }

  get stepTitle() {
    return this.data.stepTitle
  }

  get isPremier() {
    return this.subscriptionPlan === seasonTypes.PREMIER
  }

  get totalSteps() {
    return Object.keys(this.data.steps).length - 1
  }

  get seasonId() {
    return this.competitionDetails.uuid
  }
}

const createCompetitionStore = new CreateCompetitionStore()
export default createCompetitionStore

reaction(
  () => [createCompetitionStore.currentStep],
  ([currentStep]) => {
    createCompetitionStore.setStepTitle(stepTitles[currentStep])
  }
)